class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');

    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    /* PPL animated search prompts */
    this.placeholderExamples = [
  'Search patient lifts...',
  'Search sit-to-stand lifts...',
  'Search full-body lifts...',
  'Search car transfer lifts...',
  'Search patient lift slings...'
];

    this.placeholderIndex = 0;
    this.placeholderCharacter = 0;
    this.placeholderDeleting = false;
    this.placeholderTimer = null;

    this.setupEventListeners();
    this.setupAnimatedPlaceholder();
  }

  setupEventListeners() {
    this.input.form.addEventListener(
      'submit',
      this.onFormSubmit.bind(this)
    );

    this.input.addEventListener(
      'focus',
      this.onFocus.bind(this)
    );

    this.input.addEventListener(
      'blur',
      this.onInputBlur.bind(this)
    );

    this.addEventListener(
      'focusout',
      this.onFocusOut.bind(this)
    );

    this.addEventListener(
      'keyup',
      this.onKeyup.bind(this)
    );

    this.addEventListener(
      'keydown',
      this.onKeydown.bind(this)
    );
  }

  /* =======================================================
     ANIMATED PLACEHOLDER
     ======================================================= */

  setupAnimatedPlaceholder() {
    const prefersReducedMotion =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      this.input.placeholder = this.placeholderExamples[0];
      return;
    }

    if (!this.input.value.length) {
      this.startPlaceholderAnimation();
    }
  }

  startPlaceholderAnimation() {
    if (this.placeholderTimer) {
      clearTimeout(this.placeholderTimer);
    }

    if (this.input.value.length) return;

    this.animatePlaceholder();
  }

  stopPlaceholderAnimation() {
    if (this.placeholderTimer) {
      clearTimeout(this.placeholderTimer);
      this.placeholderTimer = null;
    }
  }

  animatePlaceholder() {
    if (this.input.value.length) {
      this.stopPlaceholderAnimation();
      return;
    }

    const phrase =
      this.placeholderExamples[this.placeholderIndex];

    if (!this.placeholderDeleting) {

      this.placeholderCharacter += 1;

      this.input.placeholder =
        phrase.substring(0, this.placeholderCharacter);

      if (this.placeholderCharacter >= phrase.length) {

        this.placeholderDeleting = true;

        this.placeholderTimer = setTimeout(() => {
          this.animatePlaceholder();
        }, 1900);

        return;
      }

      this.placeholderTimer = setTimeout(() => {
        this.animatePlaceholder();
      }, 55);

    } else {

      this.placeholderCharacter -= 1;

      this.input.placeholder =
        phrase.substring(0, this.placeholderCharacter);

      if (this.placeholderCharacter <= 0) {

        this.placeholderDeleting = false;

        this.placeholderIndex =
          (this.placeholderIndex + 1) %
          this.placeholderExamples.length;

        this.placeholderTimer = setTimeout(() => {
          this.animatePlaceholder();
        }, 250);

        return;
      }

      this.placeholderTimer = setTimeout(() => {
        this.animatePlaceholder();
      }, 28);
    }
  }

  onInputBlur() {
    if (!this.input.value.length) {
      this.placeholderTimer = setTimeout(() => {
        this.startPlaceholderAnimation();
      }, 350);
    }
  }

  /* =======================================================
     SEARCH
     ======================================================= */

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    if (newSearchTerm.length) {
      this.stopPlaceholderAnimation();
      this.input.placeholder = '';
    }

    if (
      !this.searchTerm ||
      !newSearchTerm.startsWith(this.searchTerm)
    ) {
      this.querySelector(
        '#predictive-search-results-groups-wrapper'
      )?.remove();
    }

    this.updateSearchForTerm(
      this.searchTerm,
      newSearchTerm
    );

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);

      this.placeholderCharacter = 0;
      this.placeholderDeleting = false;

      setTimeout(() => {
        this.startPlaceholderAnimation();
      }, 250);

      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector(
        '[aria-selected="true"] a'
      )
    ) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {

      this.searchTerm = '';

      this.abortController.abort();
      this.abortController =
        new AbortController();

      this.closeResults(true);

      this.placeholderCharacter = 0;
      this.placeholderDeleting = false;

      setTimeout(() => {
        this.startPlaceholderAnimation();
      }, 200);
    }
  }

  onFocus() {
    const currentSearchTerm =
      this.getQuery();

    /*
      Stop the typewriter while the customer
      is actively interacting with the search.
    */
    this.stopPlaceholderAnimation();

    if (!currentSearchTerm.length) {
      this.input.placeholder = 'Search patient lifts...';
      return;
    }

    if (
      this.searchTerm !==
      currentSearchTerm
    ) {
      this.onChange();
    } else if (
      this.getAttribute('results') === 'true'
    ) {
      this.open();
    } else {
      this.getSearchResults(
        this.searchTerm
      );
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        !this.contains(
          document.activeElement
        )
      ) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;

      case 'ArrowDown':
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(
    previousTerm,
    newTerm
  ) {
    const searchForTextElement =
      this.querySelector(
        '[data-predictive-search-search-for-text]'
      );

    const currentButtonText =
      searchForTextElement?.innerText;

    if (currentButtonText) {

      if (
        currentButtonText.match(
          new RegExp(previousTerm, 'g')
        )?.length > 1
      ) {
        return;
      }

      const newButtonText =
        currentButtonText.replace(
          previousTerm,
          newTerm
        );

      searchForTextElement.innerText =
        newButtonText;
    }
  }

  switchOption(direction) {
    if (
      !this.getAttribute('open')
    ) {
      return;
    }

    const moveUp =
      direction === 'up';

    const selectedElement =
      this.querySelector(
        '[aria-selected="true"]'
      );

    const allVisibleElements =
      Array.from(
        this.querySelectorAll(
          'li, button.predictive-search__item'
        )
      ).filter(
        (element) =>
          element.offsetParent !== null
      );

    let activeElementIndex = 0;

    if (
      moveUp &&
      !selectedElement
    ) {
      return;
    }

    let selectedElementIndex = -1;
    let i = 0;

    while (
      selectedElementIndex === -1 &&
      i <= allVisibleElements.length
    ) {
      if (
        allVisibleElements[i] ===
        selectedElement
      ) {
        selectedElementIndex = i;
      }

      i++;
    }

    this.statusElement.textContent = '';

    if (
      !moveUp &&
      selectedElement
    ) {

      activeElementIndex =
        selectedElementIndex ===
        allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;

    } else if (moveUp) {

      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (
      activeElementIndex ===
      selectedElementIndex
    ) {
      return;
    }

    const activeElement =
      allVisibleElements[
        activeElementIndex
      ];

    activeElement.setAttribute(
      'aria-selected',
      true
    );

    if (selectedElement) {
      selectedElement.setAttribute(
        'aria-selected',
        false
      );
    }

    this.input.setAttribute(
      'aria-activedescendant',
      activeElement.id
    );
  }

  selectOption() {
    const selectedOption =
      this.querySelector(
        '[aria-selected="true"] a, button[aria-selected="true"]'
      );

    if (selectedOption) {
      selectedOption.click();
    }
  }

  /* =======================================================
     IMPROVED PPL RETRIEVAL
     ======================================================= */

  getSearchResults(searchTerm) {
    const queryKey =
      searchTerm
        .replace(' ', '-')
        .toLowerCase();

    this.setLiveRegionLoadingState();

    if (
      this.cachedResults[queryKey]
    ) {
      this.renderSearchResults(
        this.cachedResults[queryKey]
      );

      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&resources[type]=product,query&resources[limit]=10&resources[limit_scope]=each&resources[options][unavailable_products]=last&section_id=predictive-search`,
      {
        signal:
          this.abortController.signal
      }
    )
      .then((response) => {

        if (!response.ok) {

          const error =
            new Error(
              response.status
            );

          this.close();

          throw error;
        }

        return response.text();
      })

      .then((text) => {

        const parsedHTML =
          new DOMParser()
            .parseFromString(
              text,
              'text/html'
            );

        const section =
          parsedHTML.querySelector(
            '#shopify-section-predictive-search'
          );

        if (!section) return;

        const resultsMarkup =
          section.innerHTML;

        this.allPredictiveSearchInstances.forEach(
          (
            predictiveSearchInstance
          ) => {

            predictiveSearchInstance
              .cachedResults[
                queryKey
              ] =
              resultsMarkup;
          }
        );

        this.renderSearchResults(
          resultsMarkup
        );
      })

      .catch((error) => {

        if (
          error?.code === 20 ||
          error?.name ===
            'AbortError'
        ) {
          return;
        }

        this.close();

        throw error;
      });
  }

  /* =======================================================
     LIVE REGION
     ======================================================= */

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        '.predictive-search-status'
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute(
        'data-loading-text'
      );

    this.setLiveRegionText(
      this.loadingText
    );

    this.setAttribute(
      'loading',
      true
    );
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute(
      'aria-hidden',
      'false'
    );

    this.statusElement.textContent =
      statusText;

    setTimeout(() => {

      this.statusElement.setAttribute(
        'aria-hidden',
        'true'
      );

    }, 1000);
  }

  renderSearchResults(
    resultsMarkup
  ) {
    this.predictiveSearchResults
      .innerHTML =
      resultsMarkup;

    this.setAttribute(
      'results',
      true
    );

    this.setLiveRegionResults();

    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute(
      'loading'
    );

    this.setLiveRegionText(
      this.querySelector(
        '[data-predictive-search-live-region-count-value]'
      ).textContent
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document
        .querySelector(
          '.section-header'
        )
        .getBoundingClientRect()
        .bottom;

    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults
      .style.maxHeight =
      this.resultsMaxHeight ||
      `${this.getResultsMaxHeight()}px`;

    this.setAttribute(
      'open',
      true
    );

    this.input.setAttribute(
      'aria-expanded',
      true
    );

    this.isOpen = true;
  }

  close(
    clearSearchTerm = false
  ) {
    this.closeResults(
      clearSearchTerm
    );

    this.isOpen = false;
  }

  closeResults(
    clearSearchTerm = false
  ) {
    if (clearSearchTerm) {

      this.input.value = '';

      this.removeAttribute(
        'results'
      );
    }

    const selected =
      this.querySelector(
        '[aria-selected="true"]'
      );

    if (selected) {

      selected.setAttribute(
        'aria-selected',
        false
      );
    }

    this.input.setAttribute(
      'aria-activedescendant',
      ''
    );

    this.removeAttribute(
      'loading'
    );

    this.removeAttribute(
      'open'
    );

    this.input.setAttribute(
      'aria-expanded',
      false
    );

    this.resultsMaxHeight = false;

    this.predictiveSearchResults
      .removeAttribute(
        'style'
      );
  }
}

customElements.define(
  'predictive-search',
  PredictiveSearch
);